import { ReactComponent as AlarmIcon } from '../../assets/imgs/dashBoard/dashBoard-alarm.svg';
import { ReactComponent as OnCallAlertIcon } from '../../assets/imgs/dashBoard/dashBoard-alert.svg';
import { ReactComponent as LoneWorkerTimerIcon } from '../../assets/imgs/dashBoard/dashBoard-alone-worker.svg';
import { ReactComponent as BroadcastIcon } from '../../assets/imgs/dashBoard/dashBoard-broadCast.svg';
import { ReactComponent as CecIcon } from '../../assets/imgs/dashBoard/dashBoard-call.svg';
import { ReactComponent as ChecklistIcon } from '../../assets/imgs/dashBoard/dashBoard-checkList.svg';
import { ReactComponent as DocumentIcon } from '../../assets/imgs/dashBoard/dashBoard-document.svg';
import { ReactComponent as GroupsIcon } from '../../assets/imgs/dashBoard/dashBoard-groups.svg';
import { ReactComponent as HoldingStatmentIcon } from '../../assets/imgs/dashBoard/dashBoard-holdingStatment.svg';
import { ReactComponent as ImOkIcon } from '../../assets/imgs/dashBoard/dashBoard-iamOkay.svg';
import { ReactComponent as LogNotesIcon } from '../../assets/imgs/dashBoard/dashBoard-logFile.svg';
import { ReactComponent as MessageIcon } from '../../assets/imgs/dashBoard/dashBoard-message.svg';
import { ReactComponent as NewsIcon } from '../../assets/imgs/dashBoard/dashBoard-news.svg';
import { ReactComponent as PanicButtonIcon } from '../../assets/imgs/dashBoard/dashBoard-panic.svg';
import { ReactComponent as WwwIcon } from '../../assets/imgs/dashBoard/dashBoard-www.svg';
import { ReactComponent as MeetingRoomIcon } from '../../assets/imgs/dashBoard/dashBoard-meeting-room.svg';
import type { UserPermissions } from '../../api/users';

export const getMenuItems = (
  userPermissions?: UserPermissions | null,
  orgWebsite?: string | null
): MenuItem[] => {
  if (!userPermissions) {
    return [];
  }

  const menuConfig: MenuConfigItem[] = [
    {
      Icon: AlarmIcon,
      tx: 'home_alarm',
      navLink: 'alarm',
      isEnabled: userPermissions.alarms.mayActivate,
      displayOrder: userPermissions.alarms.displayOrder,
    },
    {
      Icon: BroadcastIcon,
      tx: 'home_informationsutskick',
      navLink: 'broadcast',
      isEnabled: userPermissions.broadcast.maySend,
      displayOrder: userPermissions.broadcast.displayOrder,
    },
    {
      Icon: ChecklistIcon,
      tx: 'home_checklists',
      navLink: 'checklists',
      isEnabled: userPermissions.checklists.mayUse,
      displayOrder: userPermissions.checklists.displayOrder,
    },
    {
      Icon: CecIcon,
      tx: 'home_externalContacts',
      navLink: 'cec',
      isEnabled: userPermissions.cec.maySend,
      displayOrder: userPermissions.cec.displayOrder,
    },
    {
      Icon: DocumentIcon,
      tx: 'home_documents',
      navLink: 'documents',
      isEnabled: userPermissions.documents.mayRead,
      displayOrder: userPermissions.documents.displayOrder,
    },
    {
      Icon: GroupsIcon,
      tx: 'home_groups',
      navLink: 'groups',
      isEnabled: userPermissions.groups.mayInteractWithOwn,
      displayOrder: userPermissions.groups.displayOrder,
    },
    {
      Icon: HoldingStatmentIcon,
      tx: 'home_holdingStatement',
      navLink: 'holding-statement',
      isEnabled: userPermissions.holdingStatement.mayCreate,
      displayOrder: userPermissions.holdingStatement.displayOrder,
    },
    {
      Icon: ImOkIcon,
      tx: 'home_muster',
      navLink: 'imOk',
      isEnabled: userPermissions.imOk.mayReceive,
      displayOrder: userPermissions.imOk.displayOrder,
    },
    {
      Icon: LogNotesIcon,
      tx: 'home_logNotes',
      navLink: 'log-notes',
      isEnabled: userPermissions.logNotes.mayCreate,
      displayOrder: userPermissions.logNotes.displayOrder,
    },
    {
      Icon: LoneWorkerTimerIcon,
      navLink: 'aloneWorker/create',
      tx: 'lone_worker_timer',
      isEnabled: userPermissions.loneWorkerTimer?.mayActivate,
      displayOrder: userPermissions.loneWorkerTimer?.displayOrder,
    },
    {
      Icon: MeetingRoomIcon,
      tx: 'home_meetingRoom',
      navLink: 'meeting_room',
      isEnabled: userPermissions.meetingRoom?.mayJoin,
      displayOrder: userPermissions.meetingRoom?.displayOrder,
    },
    {
      Icon: MessageIcon,
      tx: 'home_messages',
      navLink: 'chat',
      isEnabled: userPermissions.messages.mayCreate,
      displayOrder: userPermissions.messages.displayOrder,
    },
    {
      Icon: NewsIcon,
      tx: 'home_news',
      navLink: 'news',
      isEnabled: userPermissions.news?.mayRead,
      displayOrder: userPermissions.news?.displayOrder,
    },
    {
      Icon: OnCallAlertIcon,
      tx: 'home_onCallAlerts',
      navLink: 'onCallAlert',
      isEnabled: userPermissions.onCallAlert.mayReceive,
      displayOrder: userPermissions.onCallAlert.displayOrder,
    },
    {
      Icon: PanicButtonIcon,
      navLink: 'panic/create',
      tx: 'home_panic_button',
      isEnabled: userPermissions.panicButton?.mayActivate,
      displayOrder: userPermissions.panicButton?.displayOrder,
    },
    {
      Icon: WwwIcon,
      tx: 'home_organizationWebsite',
      navLink: 'organizationWebsite',
      externalUrl: orgWebsite ?? undefined,
      isEnabled: userPermissions.www?.maySee && !!orgWebsite,
      displayOrder: userPermissions.www?.displayOrder,
    },
  ];

  return menuConfig
    .filter(item => item.isEnabled)
    .sort((a, b) => {
      if (a.displayOrder === null && b.displayOrder === null) return 0;
      if (a.displayOrder === null) return 1;
      if (b.displayOrder === null) return -1;
      return a.displayOrder - b.displayOrder;
    })
    .map(item => {
      return {
        tx: item.tx,
        navLink: item.navLink,
        externalUrl: item.externalUrl,
        Icon: item.Icon,
      };
    });
};

export type MenuItem = {
  tx: string;
  navLink: string;
  externalUrl?: string;
  Icon: React.FunctionComponent<React.SVGProps<SVGSVGElement>>;
};

type MenuConfigItem = {
  isEnabled: boolean;
  displayOrder: number | null;
  externalUrl?: string;
} & Omit<MenuItem, 'externalUrl'>;
